import * as yup from "yup";
import { isBefore, isEqual } from "date-fns";
import dayjs from "dayjs";

const FILE_SIZE = 50000000;
const SUPPORTED_IMAGE_FORMATS = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/svg+xml",
];
const SUPPORTED_FILE_FORMATS = ["file/pdf"];

export const imageValidation = (
  required = true,
  size = FILE_SIZE,
  supportedFiles = SUPPORTED_IMAGE_FORMATS,
) => {
  return yup
    .mixed()
    .nullable()
    .notRequired()
    .test("FILE_SIZE", "Image is a required", (value: any) => {
      if (!required) return true;
      return !value || value?.length > 0;
    })
    .test("FILE_SIZE", "Image file is too big.", (value: any) => {
      if (!required) return true;
      return (
        Array.from(value)?.filter(
          (item, index) => (value[index].size || 0) <= size,
        )?.length > 0
      );
    })
    .test("FILE_FORMAT", "Image file has unsupported format.", (value: any) => {
      if (!required) return true;
      return (
        Array.from(value)?.filter((item, index) =>
          supportedFiles.includes(value[index].type || "image/jpeg"),
        )?.length > 0
      );
    });
};

export const fileValidation = (
  required = true,
  size = FILE_SIZE,
  supportedFiles = SUPPORTED_FILE_FORMATS,
) =>
  yup
    .mixed()
    .nullable()
    .notRequired()
    .test("FILE_SIZE", "File is a required", (value: any) => {
      if (!required) return true;
      return !value || value?.length > 0;
    })
    .test("FILE_SIZE", "File is too big.", (value: any) => {
      if (!required) return true;
      return (
        Array.from(value)?.map(
          (item, index) => (value[index].size || 0) <= size,
        )?.length > 0
      );
    })
    .test("FILE_FORMAT", "File has unsupported format.", (value: any) => {
      if (!required) return true;
      return (
        Array.from(value)?.map((item, index) =>
          supportedFiles.includes(value[index].type || "image/pdf"),
        )?.length > 0
      );
    });

export const richTextValidation = <T extends string>() =>
  yup
    .mixed<T>()
    .nullable()
    .notRequired()
    .test("RICH_TEXT", "This field is required", (value: any) => {
      const valueWithoutHtmlTag = value?.replace(/(<([^>]+)>)/gi, "");
      return value || valueWithoutHtmlTag?.length > 0;
    })
    .test("RICH_TEXT", "This field is required", (value: any) => {
      const valueWithoutHtmlTag = value?.replace(/(<([^>]+)>)/gi, "");
      return valueWithoutHtmlTag;
    });

export function pastDateOnlyValidation(this: any, value: string | undefined) {
  const required = this.schema.spec.optional;
  if (!value && required) return true;

  const currentDate = new Date().toISOString().substring(0, 10);
  const validationBefore = isBefore(
    new Date(value as string),
    new Date(currentDate),
  );

  const validationEqual = isEqual(
    new Date(value as string),
    new Date(currentDate),
  );

  if (validationBefore && !validationEqual) return true;
  else
    return this.createError({
      message: "Please choose a date prior to today",
    });
}

export function pastDateWithTodayValidation(
  this: any,
  value: string | undefined,
) {
  const required = this.schema.spec.optional;
  if (!value && required) return true;

  const currentDate = new Date().toISOString().substring(0, 10);
  const validationBefore = isBefore(
    new Date(value as string),
    new Date(currentDate),
  );

  const validationEqual = isEqual(
    new Date(value as string),
    new Date(currentDate),
  );

  if (validationBefore || validationEqual) return true;
  else
    return this.createError({
      message: "Please choose a date from the past or today",
    });
}

export function futureDateOnlyValidation(this: any, value: string | undefined) {
  const required = this.schema.spec.optional;
  if (!value && required) return true;

  const currentDate = new Date().toISOString().substring(0, 10);
  const validationBefore = isBefore(
    new Date(value as string),
    new Date(currentDate),
  );

  const validationEqual = isEqual(
    new Date(value as string),
    new Date(currentDate),
  );

  if (!validationBefore && !validationEqual) return true;
  else
    return this.createError({
      message: "Please choose a future date",
    });
}

export function futureDateWithTodayValidation(
  this: any,
  value: string | undefined,
) {
  const required = this.schema.spec.optional;
  if (!value && required) return true;

  const currentDate = new Date().toISOString().substring(0, 10);
  const validationBefore = isBefore(
    new Date(value as string),
    new Date(currentDate),
  );

  if (!validationBefore) return true;
  else
    return this.createError({
      message: "Please choose a future date or today",
    });
}

export const dateRangeValidation = (
  startDateOrTime: string,
  endDateOrTime: string,
  schema: any,
  type?: any,
  errorMsg?: string,
): any => {
  const start = schema.parent[startDateOrTime] || "";
  const end = schema.parent[endDateOrTime] || "";

  const isAfter = dayjs(end).isAfter(start, type || "day");

  if (!isAfter) {
    return schema.createError({
      message: errorMsg ? errorMsg : "End date is required & greater than start date",
    });
  } else {
    return true;
  }
};
